import React, { useEffect, useMemo, useRef, useState } from "react";

/**
 * MultiSelectDropdown
 * A lightweight multi-select dropdown with optional "Select All" capability.
 *
 * Props:
 * - value: string[]          // selected values
 * - options: {label:string, value:string}[]
 * - onChange: (values:string[]) => void
 * - placeholder?: string
 * - className?: string
 * - maxSelectedLabels?: number // if provided, shows up to N chips then "+X"
 * - showSelectAll?: boolean
 * - selectAllLabel?: string
 */
function MultiSelectDropdown({
  value,
  options,
  onChange,
  placeholder,
  className,
  maxSelectedLabels,
  showSelectAll = true,
  selectAllLabel = "Select All",
}) {
  const containerRef = useRef(null);
  const [open, setOpen] = useState(false);

  const normalizedOptions = useMemo(() => Array.isArray(options) ? options : [], [options]);
  const selectedSet = useMemo(() => new Set(Array.isArray(value) ? value : []), [value]);

  const allValues = useMemo(() => normalizedOptions.map(o => o.value), [normalizedOptions]);
  const allSelected = useMemo(() => allValues.length > 0 && allValues.every(v => selectedSet.has(v)), [allValues, selectedSet]);

  const visibleSelectedLabels = useMemo(() => {
    const labels = normalizedOptions.filter(o => selectedSet.has(o.value)).map(o => o.label);
    if (typeof maxSelectedLabels === "number" && labels.length > maxSelectedLabels) {
      const shown = labels.slice(0, maxSelectedLabels);
      const remaining = labels.length - maxSelectedLabels;
      return [...shown, `+${remaining}`];
    }
    return labels;
  }, [normalizedOptions, selectedSet, maxSelectedLabels]);

  const toggleOpen = () => setOpen(prev => !prev);

  const toggleValue = (val) => {
    const current = new Set(selectedSet);
    if (current.has(val)) {
      current.delete(val);
    } else {
      current.add(val);
    }
    onChange(Array.from(current));
  };

  const handleSelectAllToggle = () => {
    if (allSelected) {
      onChange([]);
    } else {
      onChange(allValues);
    }
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div ref={containerRef} className={`msd-container ${className || ""}`.trim()}>
      <button type="button" className="msd-control form-input" onClick={toggleOpen} aria-haspopup="listbox" aria-expanded={open}>
        <div className="msd-value">
          {visibleSelectedLabels.length > 0 ? (
            <div className="msd-chips">
              {visibleSelectedLabels.map((label, idx) => (
                <span key={idx} className="msd-chip">{label}</span>
              ))}
            </div>
          ) : (
            <span className="msd-placeholder">{placeholder || "Select"}</span>
          )}
        </div>
        <span className="msd-caret">▾</span>
      </button>

      {open && (
        <div className="msd-panel">
          {showSelectAll && (
            <div className="msd-option msd-select-all">
              <label className="msd-option-label">
                <input type="checkbox" checked={allSelected} onChange={handleSelectAllToggle} />
                <span>{selectAllLabel}</span>
              </label>
            </div>
          )}
          <div className="msd-options">
            {normalizedOptions.map((opt) => (
              <div key={opt.value} className="msd-option">
                <label className="msd-option-label">
                  <input
                    type="checkbox"
                    checked={selectedSet.has(opt.value)}
                    onChange={() => toggleValue(opt.value)}
                  />
                  <span>{opt.label}</span>
                </label>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Minimal styles; integrate with FormImprovements.css via class hooks */}
      <style>
        {`
        .msd-container { position: relative; width: 100%; }
        .msd-control { width: 100%; display: flex; align-items: center; justify-content: space-between; cursor: pointer; background: #fff; border: 1px solid #ced4da; border-radius: 6px; padding: 8px 10px; }
        .msd-placeholder { color: #6c757d; }
        .msd-chips { display: flex; flex-wrap: wrap; gap: 6px; }
        .msd-chip { background: #f1f3f5; border: 1px solid #e9ecef; padding: 2px 8px; border-radius: 12px; font-size: 12px; }
        .msd-caret { margin-left: 8px; }
        .msd-panel { position: absolute; z-index: 1000; top: calc(100% + 4px); left: 0; right: 0; background: #fff; border: 1px solid #dee2e6; border-radius: 6px; max-height: 300px; overflow: auto; box-shadow: 0 6px 20px rgba(0,0,0,0.08); }
        .msd-option { padding: 8px 10px; }
        .msd-option + .msd-option { border-top: 1px solid #f1f3f5; }
        .msd-option-label { display: flex; align-items: center; gap: 8px; cursor: pointer; }
        .msd-select-all { background: #fafafa; border-bottom: 1px solid #f1f3f5; }
        `}
      </style>
    </div>
  );
}

export default MultiSelectDropdown;
